import { HomeIcon } from '@heroicons/react/24/outline'
import {
  BreadcrumbItem,
  Breadcrumbs,
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
} from '@heroui/react'
import { MediaType, queryKeys, useBulkMoveFiles } from '@qse/api'
import { AppRoutesList } from '@qse/constants'
import { useIsOverQuota } from '@qse/stores'
import { FileTypeIcon, toast, useGlobalFileUpload, useTheme } from '@qse/ui'
import { useQueryClient } from '@tanstack/react-query'
import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'

interface BreadcrumbItem {
  id: string
  name: string
  path?: string
}

interface BreadcrumbProps {
  items: BreadcrumbItem[]
  onItemClick?: (item: BreadcrumbItem) => void
  onDrop?: (fileIds: string[], targetFolderId: string) => void
  loading?: boolean
  isModal?: boolean
}

const Breadcrumb: React.FC<BreadcrumbProps> = ({
  items,
  onItemClick,
  onDrop,
  loading,
  isModal = false,
}) => {
  const navigate = useNavigate()
  const [dragOverItem, setDragOverItem] = useState<string | null>(null)
  const { mutate: moveFiles } = useBulkMoveFiles()
  const { addFiles } = useGlobalFileUpload()
  const queryClient = useQueryClient()
  const { theme } = useTheme()
  const isOverQuota = useIsOverQuota()

  const handleItemClick = (item: BreadcrumbItem) => {
    if (onItemClick) {
      onItemClick(item)
    } else if (item.path) {
      navigate(item.path)
    } else if (item.id === 'root') {
      navigate(AppRoutesList.dashboard)
    }
  }

  const handleDragOver = (e: React.DragEvent, itemId: string) => {
    e.preventDefault()
    // Check if this is an external drag (upload) vs internal drag (move)
    // External drags have 'Files' type but not 'text/plain' (our internal move marker)
    const isExternalDrag =
      e.dataTransfer.types.includes('Files') && !e.dataTransfer.types.includes('text/plain')

    // Prevent drag-over visual feedback for external uploads when over quota
    // Internal moves are allowed since they don't increase quota
    if (isOverQuota && isExternalDrag) {
      e.dataTransfer.dropEffect = 'none'

      return
    }
    e.dataTransfer.dropEffect = 'move'
    setDragOverItem(itemId)
  }

  const handleDragLeave = () => {
    setDragOverItem(null)
  }

  const handleDrop = (e: React.DragEvent, itemId: string) => {
    e.preventDefault()
    setDragOverItem(null)
    const item = items.find(i => i.id === itemId)
    const itemPath = item?.path

    // Try to move files (from FileExplorer)
    try {
      const draggedIds = JSON.parse(e.dataTransfer.getData('text/plain'))

      if (Array.isArray(draggedIds) && draggedIds.length > 0) {
        // Validate: Prevent dropping a folder into itself or its own path
        const isDroppingFolderIntoItself = draggedIds.some((draggedId: string | number) => {
          // Check if any dragged item is the same as the target breadcrumb item
          return draggedId.toString() === itemId
        })

        if (isDroppingFolderIntoItself) {
          toast.error('Cannot move a folder into itself')

          return
        }

        moveFiles(
          {
            fileIds: draggedIds.map(String),
            targetParentPath:
              itemPath && itemId !== 'root' ? itemPath : itemId === 'root' ? '/' : undefined,
          },
          {
            onError: (err: unknown) => {
              const errorMessage = err instanceof Error ? err.message : 'Failed to move files'

              toast.error(errorMessage)
            },
          },
        )

        return
      }
    } catch {
      // Ignore parsing errors
    }

    // Try to upload files (from OS) - prevent if over quota
    const files = Array.from(e.dataTransfer.files)

    if (files.length > 0) {
      if (isOverQuota) {
        toast.error('Storage quota exceeded. Delete files to free up space.')

        return
      }
      // Process files including folders
      const processedFiles = files.map(file => {
        // Check if it's a folder by looking at the webkitRelativePath
        const isFolder = file.webkitRelativePath && file.webkitRelativePath.split('/').length > 1

        if (isFolder) {
          // Extract the relative path from the file's webkitRelativePath
          const relativePath = file.webkitRelativePath
          const pathParts = relativePath.split('/')

          pathParts.pop() // Get the filename
          const folderPath = pathParts.join('/') // Get the folder path

          return {
            ...file,
            path: folderPath || undefined, // Set the path for folder structure preservation
          }
        }

        return file
      })

      // Use the full backend path for the breadcrumb item as parentPath
      let parentPath: string | undefined = undefined

      if (itemPath && itemId !== 'root') {
        parentPath = itemPath // Use the full backend path
      } else if (itemId === 'root') {
        parentPath = '/'
      }

      addFiles(
        processedFiles,
        undefined, // parentId
        [itemId ? queryKeys.files.folderDetails(itemId) : queryKeys.files.lists()],
        queryClient,
        parentPath, // parentPath as last parameter
      )

      return
    }

    // Fallback to onDrop if provided
    if (onDrop) {
      onDrop([], itemId)
    }
  }

  // Create a wrapper div that handles drag and drop events
  const DragDropBreadcrumbItem: React.FC<{
    children: React.ReactNode
    itemId: string
    onClick: () => void
    itemPath?: string
  }> = ({ children, itemId, onClick, itemPath }) => {
    // Check if this item is the current active folder
    const isCurrentActiveFolder = itemPath === currentActiveFolder?.path

    return (
      <button
        type="button"
        className={`cursor-pointer transition-opacity ${isModal ? 'px-2 py-1' : 'px-4 py-3'} rounded-md 
          ${
            dragOverItem === itemPath && !isCurrentActiveFolder
              ? 'bg-blue-100 dark:bg-blue-500/20 rounded'
              : ''
          }`}
        onClick={onClick}
        onDragLeave={handleDragLeave}
        onDragOver={e => {
          // Don't show drag feedback if this is the current active folder
          if (!isCurrentActiveFolder) {
            handleDragOver(e, itemPath || '')
          }
        }}
        onDrop={e => {
          // Don't allow dropping if this is the current active folder
          if (!isCurrentActiveFolder) {
            handleDrop(e, itemId)
          }
        }}
      >
        {children}
      </button>
    )
  }

  if (loading) {
    return (
      <div className={`flex items-center space-x-2 ${isModal ? 'text-sm mb-2' : 'text-base mb-1'}`}>
        <div className="flex items-center gap-2 animate-pulse">
          <div
            className={`${isModal ? 'w-5 h-5' : 'w-7 h-7'} rounded bg-gray-300 dark:bg-gray-700`}
          />
          <span
            className={`font-medium ${isModal ? 'w-20 h-4' : 'w-24 h-5'} rounded bg-gray-200 dark:bg-gray-800 block`}
          />
        </div>
      </div>
    )
  }

  // Prepare all breadcrumb items including Home
  const rootPath = '/home'
  const allItems = [{ id: 'root', name: 'Home', path: rootPath }, ...items]

  // Log the current active folder (last folder in breadcrumb)
  const currentActiveFolder = items.length > 0 ? items[items.length - 1] : null

  return (
    <div className={isModal ? 'mb-2' : 'mb-1'}>
      <Breadcrumbs
        itemsAfterCollapse={2}
        itemsBeforeCollapse={1}
        maxItems={4}
        size={isModal ? 'md' : 'lg'}
        classNames={{
          list: isModal ? 'gap-1' : 'gap-3',
          separator: `${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`,
        }}
        renderEllipsis={({ items, ellipsisIcon, separator }) => (
          <div className="flex items-center">
            <Dropdown>
              <DropdownTrigger>
                <Button
                  isIconOnly
                  className={isModal ? 'min-w-5 w-5 h-5' : 'min-w-6 w-6 h-6'}
                  size="sm"
                  variant="flat"
                >
                  {ellipsisIcon}
                </Button>
              </DropdownTrigger>
              <DropdownMenu aria-label="Hidden breadcrumb items">
                {items.map((_, index) => {
                  const breadcrumbItem = allItems[index + 1]

                  return (
                    <DropdownItem
                      key={index}
                      onPress={() => breadcrumbItem && handleItemClick(breadcrumbItem)}
                    >
                      <div className={`flex items-center ${isModal ? 'gap-1' : 'gap-2'}`}>
                        <FileTypeIcon
                          mediaType={MediaType.FOLDER}
                          size={isModal ? 20 : 24}
                          className={
                            isModal ? 'sm:w-[24px] sm:h-[24px]' : 'sm:w-[30px] sm:h-[30px]'
                          }
                        />
                        <span
                          className={`${isModal ? 'text-sm' : ''} block`}
                          title={breadcrumbItem?.name}
                        >
                          {breadcrumbItem?.name && breadcrumbItem.name.length > 20
                            ? `${breadcrumbItem.name.slice(0, 20)}...`
                            : breadcrumbItem?.name}
                        </span>
                      </div>
                    </DropdownItem>
                  )
                })}
              </DropdownMenu>
            </Dropdown>
            {separator}
          </div>
        )}
      >
        {/* Home item with icon */}
        <BreadcrumbItem>
          <DragDropBreadcrumbItem
            itemId="root"
            itemPath={rootPath}
            onClick={() => handleItemClick({ id: 'root', name: 'Home' })}
          >
            <div className="flex items-center">
              <HomeIcon className="w-6 h-6 mr-3 -mt-1 inline-block dark:text-gray-300 text-blue-700" />
              <span
                className={`font-medium block ${isModal ? 'text-base' : 'text-lg'} ${theme === 'dark' ? 'text-gray-300' : 'text-blue-700'}`}
              >
                Home
              </span>
            </div>
          </DragDropBreadcrumbItem>
        </BreadcrumbItem>

        {/* Regular breadcrumb items */}
        {items.map(item => (
          <BreadcrumbItem key={item.id}>
            <DragDropBreadcrumbItem
              itemId={item.id}
              itemPath={item.path}
              onClick={() => handleItemClick(item)}
            >
              <span className={isModal ? 'text-sm' : 'text-base'} title={item.name}>
                {item.name.length > 20 ? `${item.name.slice(0, 20)}...` : item.name}
              </span>
            </DragDropBreadcrumbItem>
          </BreadcrumbItem>
        ))}
      </Breadcrumbs>
    </div>
  )
}

export default Breadcrumb
